'use client';

import React from 'react';

import { Box, BoxProps } from '../box';
import { Stack } from '../stack';

export interface TimelineProps extends BoxProps {
  /** Timeline orientation */
  orientation?: 'vertical' | 'horizontal';
  /** Whether to show connectors between items */
  showConnectors?: boolean;
  /** Connector style */
  connectorStyle?: 'solid' | 'dashed' | 'dotted';
  /** Connector color */
  connectorColor?: string;
  /** Timeline alignment for vertical orientation */
  alignment?: 'left' | 'center' | 'right' | 'alternate';
  /** Spacing between items */
  spacing?: number;
  /** Children timeline items */
  children: React.ReactNode;
}

/**
 * Timeline component - displays events in chronological order
 * Container for TimelineItem components
 */
export const Timeline: React.FC<TimelineProps> = ({
  orientation = 'vertical',
  showConnectors = true,
  connectorStyle = 'solid',
  connectorColor = 'border.muted',
  alignment = 'left',
  spacing = 4,
  children,
  ...boxProps
}) => {
  const isVertical = orientation === 'vertical';
  const isAlternate = alignment === 'alternate';

  // Count children to know which is last
  const childrenArray = React.Children.toArray(children);
  const childCount = childrenArray.length;

  // Clone children with additional props
  const enhancedChildren = React.Children.map(children, (child, index) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<Record<string, unknown>>, {
        orientation,
        showConnector: showConnectors && index < childCount - 1,
        connectorStyle,
        connectorColor,
        alignment: isAlternate ? (index % 2 === 0 ? 'right' : 'left') : alignment,
        isFirst: index === 0,
        isLast: index === childCount - 1,
        index,
      });
    }
    return child;
  });

  return (
    <Box position="relative" {...boxProps}>
      {/* Center line for alternate alignment */}
      {isVertical && isAlternate && showConnectors && (
        <Box
          position="absolute"
          left="50%"
          top={0}
          bottom={0}
          width="2px"
          backgroundColor={connectorColor}
          transform="translateX(-50%)"
          borderStyle={connectorStyle}
          zIndex={0}
        />
      )}

      <Stack
        direction={isVertical ? 'column' : 'row'}
        gap={spacing}
        alignItems={isVertical ? 'stretch' : 'center'}
        position="relative"
        zIndex={1}
      >
        {enhancedChildren}
      </Stack>
    </Box>
  );
};

export default Timeline;
